import React from 'react';
import { Search, ArrowLeft, History, X } from 'lucide-react';

interface SearchViewProps {
  onClose: () => void;
}

const PLACEHOLDER_TEXTS = ['Search Biden...', 'Search Trump...', 'Search Kamala...'];

export function SearchView({ onClose }: SearchViewProps) {
  const [recentSearches, setRecentSearches] = React.useState<string[]>(() => {
    const saved = localStorage.getItem('recentSearches');
    return saved ? JSON.parse(saved) : [];
  });
  const [searchTerm, setSearchTerm] = React.useState('');
  const [placeholderIndex, setPlaceholderIndex] = React.useState(0);
  
  // Animate placeholder text
  React.useEffect(() => {
    const interval = setInterval(() => {
      setPlaceholderIndex(prev => (prev + 1) % PLACEHOLDER_TEXTS.length);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const addToRecentSearches = (term: string) => {
    const trimmed = term.trim();
    if (!trimmed) return;
    
    setRecentSearches(prev => {
      const newSearches = [trimmed, ...prev.filter(s => s !== trimmed)].slice(0, 10);
      localStorage.setItem('recentSearches', JSON.stringify(newSearches));
      return newSearches;
    });
  };

  const removeSearch = (search: string) => {
    setRecentSearches(prev => {
      const newSearches = prev.filter(s => s !== search);
      localStorage.setItem('recentSearches', JSON.stringify(newSearches));
      return newSearches;
    });
  };

  const handleSearch = (term: string) => {
    addToRecentSearches(term);
    // TODO: Implement actual search
    console.log('Searching for:', term);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      handleSearch(searchTerm);
      setSearchTerm('');
    }
  };

  return (
    <div className="fixed inset-0 bg-black z-50 safe-area-top">
      <form onSubmit={handleSubmit} className="flex items-center gap-2 p-4 border-b border-gray-800">
        <button type="button" onClick={onClose} className="p-2">
          <ArrowLeft className="w-6 h-6 text-white" />
        </button>
        <div className="flex-1 flex items-center gap-2 bg-gray-800/50 rounded-full px-3 py-2">
          <Search className="w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={PLACEHOLDER_TEXTS[placeholderIndex]}
            className="bg-transparent text-white w-full outline-none text-sm placeholder-gray-500"
            autoFocus
          />
        </div>
      </form>

      <div className="p-4">
        {recentSearches.length > 0 && (
          <>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <History className="w-5 h-5 text-gray-400" />
                <h2 className="text-white font-semibold">Recent Searches</h2>
              </div>
              <button
                onClick={() => {
                  setRecentSearches([]);
                  localStorage.removeItem('recentSearches');
                }}
                className="text-gray-400 text-sm hover:text-white transition-colors"
              >
                Clear All
              </button>
            </div>
            
            <div className="space-y-2">
              {recentSearches.map((search) => (
                <div
                  key={search}
                  className="flex items-center justify-between py-2 border-b border-gray-800"
                >
                  <button
                    className="text-white text-sm flex-1 text-left hover:text-blue-400 transition-colors"
                    onClick={() => {
                      setSearchTerm(search);
                      handleSearch(search);
                    }}
                  >
                    {search}
                  </button>
                  <button
                    onClick={() => removeSearch(search)}
                    className="p-1 text-gray-400 hover:text-white transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          </>
        )}
        
        {recentSearches.length === 0 && (
          <div className="flex flex-col items-center justify-center h-48 text-gray-400">
            <Search className="w-12 h-12 mb-4 opacity-50" />
            <p className="text-center">Search for videos, users, or hashtags</p>
          </div>
        )}
      </div>
    </div>
  );
}